import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { fmtDate } from "@/lib/format"
import type { DayInfo } from "@/types"
import { DAY_TYPE_META } from "./dayTypes"

interface DayInfoPopoverProps {
  day: DayInfo
  /** Zellen-Element, an dem das Popover verankert wird */
  anchor: HTMLElement
  onClose: () => void
}

/** Leichtgewichtiges Popover fuer die Tagesdetails (ohne Radix-Popover-Dependency). */
export function DayInfoPopover({ day, anchor, onClose }: DayInfoPopoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const rect = anchor.getBoundingClientRect()
    const width = 288 // w-72
    let left = rect.left + rect.width / 2 - width / 2
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    let top = rect.bottom + 6
    const estimatedHeight = 190
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - 6)
    }
    setPos({ top, left })
  }, [anchor])

  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node) && e.target !== anchor) {
        onClose()
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [anchor, onClose])

  if (!pos) return null
  const meta = DAY_TYPE_META[day.dayType]

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Tagesinfo ${fmtDate(day.date)}`}
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{fmtDate(day.date)}</span>
        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${meta.cellClass}`}>
          {meta.label}
        </span>
      </div>
      <dl className="space-y-1.5 text-sm">
        {day.holidayName && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">Feiertag</dt>
            <dd className="text-right font-medium">{day.holidayName}</dd>
          </div>
        )}
        {day.note && (
          <div>
            <dt className="text-muted-foreground">Hinweis</dt>
            <dd className="mt-0.5 whitespace-pre-wrap">{day.note}</dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <dt className="text-muted-foreground">Planbar</dt>
          <dd>
            <Badge variant={day.isPlannable ? "success" : "secondary"}>
              {day.isPlannable ? "Ja" : "Nein"}
            </Badge>
          </dd>
        </div>
        {day.isCompanyClosure && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">Betriebsurlaub</dt>
            <dd>
              <Badge className="border-transparent bg-blue-100 text-blue-800 hover:bg-blue-100">
                Ja
              </Badge>
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}
